import { useFeedback, useSource } from "@opendash/core";
import { useTranslation } from "@opendash/i18n";
import { useUIPermission } from "@opendash/plugin-parse";
import { Source } from "@opendash/plugin-parse/dist/types-generated";
import { Drawer, Segmented } from "antd";
import { SegmentedOptions } from "antd/es/segmented";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { OpenService } from "../../../types/config";
import { MessageForm } from "../../messages/components/MessageForm";
import ScheduleExecutionForm from "../../schedules/components/ScheduleExecutionForm";
import { TicketSaveForm } from "../../tickets";
import { MachinelogContext } from "../context/MachinelogContext";

type Props = {
  createOpen: boolean;
  setCreateOpen: (flag: boolean) => void;
};

export default observer((props: Props) => {
  const [sourceInterface] = useSource();
  const [segmentedValue, setSegmentedValue] = useState<number>(0);
  const [source, setSource] = useState<Source>(null);
  const t = useTranslation();
  const { message } = useFeedback();
  const canCreateTicket = useUIPermission(
    [
      OpenService.Permissions.Create.ticket,
      OpenService.Permissions.Create.machinelog_ticket,
    ],
    true
  );
  const canCreateSchedule = useUIPermission(
    [
      OpenService.Permissions.Create.schedule,
      OpenService.Permissions.Create.machinelog_schedule,
    ],
    true
  );
  const canCreateMessage = useUIPermission(
    [
      OpenService.Permissions.Create.message,
      OpenService.Permissions.Create.machinelog_message,
    ],
    true
  );

  const segmentedOptions: SegmentedOptions<React.SetStateAction<number>> =
    useMemo(() => {
      const options: SegmentedOptions<React.SetStateAction<number>> = [];
      canCreateTicket &&
        options.push({
          label: t("maintenance:machinelog.create.ticket"),
          value: 0,
        });
      canCreateSchedule &&
        options.push({
          label: t("maintenance:machinelog.create.schedule"),
          value: 1,
        });
      canCreateMessage &&
        options.push({
          label: t("maintenance:machinelog.create.message"),
          value: 2,
        });
      return options;
    }, [canCreateMessage, canCreateSchedule, canCreateTicket]);

  const machinelogState = useContext(MachinelogContext);

  useEffect(() => {
    if (sourceInterface?.id) {
      new Parse.Query(Source)
        .equalTo("objectId", sourceInterface?.id)
        .first()
        .then(setSource);
    }
  }, [sourceInterface]);

  function onSave() {
    message.success(t("maintenance:machinelog.create.success"));
    props.setCreateOpen(false);
    machinelogState.reload();
  }

  function onError() {
    message.error(t("maintenance:machinelog.create.error"));
    props.setCreateOpen(false);
  }

  return (
    <Drawer
      open={props.createOpen}
      size="large"
      closable
      onClose={() => props.setCreateOpen(false)}
      title={t("maintenance:machinelog.create.headline")}
      placement="bottom"
    >
      <Segmented
        options={segmentedOptions}
        block
        value={segmentedValue}
        onChange={setSegmentedValue}
        style={{ marginBottom: 12 }}
      />

      {/* Show form for Ticket */}
      {segmentedValue === 0 ? (
        <TicketSaveForm
          onSaveSuccess={onSave}
          initialValues={{ source: source?.id }}
        />
      ) : null}

      {/* Show form for Schedule */}
      {segmentedValue === 1 ? (
        <ScheduleExecutionForm
          sources={source ? [source] : []}
          reload={segmentedValue}
          onSaveSuccess={onSave}
          dateField
        />
      ) : null}

      {/* Show form for generic message */}
      {segmentedValue === 2 ? (
        <MessageForm
          className={Source.className}
          referencedObject={sourceInterface?.id}
          onSave={onSave}
          onError={onError}
        />
      ) : null}
    </Drawer>
  );
});
